<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div style="width: 100%; max-width: 1200px;">

      <!-- Sticky Header -->
      <div class="table-header-filters pa-4 mb-6">
        <div class="d-flex align-center">
          <v-icon size="20" color="#00838F" class="mr-2">mdi-history</v-icon>
          <div>
            <div class="page-title">Log slanja</div>
            <div v-if="reportType" class="page-subtitle">{{ reportType }}</div>
          </div>
          <div class="ml-auto">
            <v-btn icon class="close-btn" :to="backRoute">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="cyan darken-1" size="48" />
        <div class="mt-4 grey--text">Učitavanje logova...</div>
      </div>

      <!-- Empty -->
      <div v-else-if="jobs.length === 0" class="empty-state">
        <v-icon size="56" color="#CFD8DC">mdi-email-check-outline</v-icon>
        <p class="mt-3 grey--text">Nema zabilježenih slanja za ovu pretplatu.</p>
      </div>

      <!-- Jobs table -->
      <div v-else class="glass-table-wrapper">
        <table>
          <thead>
            <tr class="naslov">
              <th class="text-left">Datum izvještaja</th>
              <th class="text-left">Status</th>
              <th class="text-left">Stvoreno</th>
              <th class="text-left">Poslano</th>
              <th class="text-left">Greška</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="job in jobs" :key="job.id">
              <td>{{ job.scheduledDate || '—' }}</td>
              <td>
                <span class="status-badge" :class="`status-${job.status}`">
                  {{ statusLabel(job.status) }}
                </span>
              </td>
              <td class="mono">{{ formatDt(job.createdAt) }}</td>
              <td class="mono">{{ job.sentAt ? formatDt(job.sentAt) : '—' }}</td>
              <td class="error-reason">{{ job.errorReason || '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  </v-container>
</template>

<script>
import state from '@/state'
import EventBus from '@/plugins/event-bus'
import { getSubscriptionJobs } from '@/api/emailReportScheduler'

const STATUS_LABELS = {
  pending: 'Na čekanju',
  processing: 'U obradi',
  sent: 'Poslano',
  generation_failed: 'Greška generiranja',
  send_failed: 'Greška slanja'
}

export default {
  name: 'AutomatskiSlanjeLog',
  inject: ['showMsgBox'],

  data () {
    return {
      margin: '0px',
      loading: false,
      jobs: []
    }
  },

  computed: {
    companyId () {
      return this.$route.params.companyId
    },
    subscriptionIds () {
      const raw = this.$route.query.ids || ''
      return raw.split(',').filter(Boolean)
    },
    reportType () {
      return this.$route.query.type || ''
    },
    backRoute () {
      return {
        name: 'company.all-reports.automatski-slanje',
        params: { companyId: this.companyId }
      }
    }
  },

  mounted () {
    this.margin = state.getMargin()
    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })
    this.loadJobs()
  },

  beforeDestroy () {
    EventBus.$off('set-mini')
  },

  methods: {
    async loadJobs () {
      if (!this.subscriptionIds.length) return
      this.loading = true
      try {
        const results = await Promise.all(
          this.subscriptionIds.map(id => getSubscriptionJobs(id))
        )
        const all = results.flat()
        all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        this.jobs = all
      } catch (e) {
        this.showMsgBox({ text: 'Greška pri učitavanju logova.', color: 'error', actions: ['cancel'], cancelBtnText: 'OK' })
      } finally {
        this.loading = false
      }
    },

    statusLabel (status) {
      return STATUS_LABELS[status] || status || '—'
    },

    formatDt (val) {
      if (!val) return '—'
      const d = new Date(val)
      if (isNaN(d)) return '—'
      return d.toLocaleString('hr-HR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.page-title {
  font-size: 18px;
  font-weight: 700;
  color: #2d3748;
  letter-spacing: 0.3px;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 12px;
  color: #9CA3AF;
  font-weight: 500;
  margin-top: 2px;
}

.empty-state {
  text-align: center;
  padding: 60px 0;
}

/* ── Sticky header ── */
.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

/* ── Close button ── */
.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}

.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}

/* ── Table ── */
.glass-table-wrapper {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.glass-table-wrapper table {
  border-collapse: collapse;
  width: 100%;
}

.glass-table-wrapper table th,
.glass-table-wrapper table td {
  padding: 12px 20px;
  border: none;
  border-bottom: 1px solid #EDF2F7;
  font-size: 13px;
}

.glass-table-wrapper table tbody tr:last-child td {
  border-bottom: none;
}

.glass-table-wrapper table tbody tr:hover {
  background: #F7FAFC;
}

.naslov th {
  background: #F8FAFC;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #9CA3AF;
}

.mono {
  font-family: monospace;
  font-size: 12px;
  color: #6B7280;
}

.error-reason {
  font-size: 12px;
  color: #E53935;
  max-width: 300px;
}

/* ── Status badges ── */
.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.status-pending {
  background: rgba(158, 158, 158, 0.12);
  color: #616161;
  border: 1px solid rgba(158, 158, 158, 0.3);
}

.status-processing {
  background: rgba(57, 137, 201, 0.1);
  color: #1565C0;
  border: 1px solid rgba(57, 137, 201, 0.3);
}

.status-sent {
  background: rgba(67, 160, 71, 0.1);
  color: #2E7D32;
  border: 1px solid rgba(67, 160, 71, 0.3);
}

.status-generation_failed,
.status-send_failed {
  background: rgba(229, 57, 53, 0.08);
  color: #B71C1C;
  border: 1px solid rgba(229, 57, 53, 0.3);
}
</style>
